<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import InternalCommunicationAPI from 'dashboard/api/internalCommunication';
import Button from 'dashboard/components-next/button/Button.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

const props = defineProps({
  referenceId: {
    type: Number,
    required: true,
  },
  accountId: {
    type: [String, Number],
    required: true,
  },
  essentialMode: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const router = useRouter();
const reference = ref(null);

const conversationStatusLabels = computed(() => ({
  open: t('INTERNAL_COMMUNICATION.REFERENCE.STATUS.OPEN'),
  resolved: t('INTERNAL_COMMUNICATION.REFERENCE.STATUS.RESOLVED'),
  pending: t('INTERNAL_COMMUNICATION.REFERENCE.STATUS.PENDING'),
  snoozed: t('INTERNAL_COMMUNICATION.REFERENCE.STATUS.SNOOZED'),
}));

const formatConversationStatus = status => {
  return conversationStatusLabels.value[status] || status;
};

onMounted(async () => {
  try {
    const { data } =
      await InternalCommunicationAPI.resolveConversationReference(
        props.referenceId
      );
    reference.value = data;
  } catch {
    reference.value = { available: false };
  }
});

const openConversation = () => {
  if (!reference.value?.available) return;
  const path = props.essentialMode
    ? `accounts/${props.accountId}/essential/conversations/${reference.value.display_id}`
    : `accounts/${props.accountId}/conversations/${reference.value.display_id}`;
  router.push(frontendURL(path));
};
</script>

<template>
  <div class="mt-2 max-w-md rounded-xl border border-n-weak bg-n-alpha-1 p-3">
    <p v-if="!reference" class="m-0 text-xs text-n-slate-11">
      {{ t('INTERNAL_COMMUNICATION.REFERENCE.LOADING') }}
    </p>
    <div v-else-if="reference.available" class="grid gap-2">
      <div class="flex items-center justify-between gap-3">
        <div class="min-w-0">
          <p class="m-0 truncate text-sm font-semibold text-n-slate-12">
            {{ reference.contact.name }}
          </p>
          <p class="m-0 text-xs text-n-slate-11">
            {{
              t('INTERNAL_COMMUNICATION.REFERENCE.TITLE', {
                id: reference.display_id,
              })
            }}
          </p>
        </div>
        <span class="rounded-md bg-n-slate-3 px-2 py-1 text-xs text-n-slate-11">
          {{ formatConversationStatus(reference.status) }}
        </span>
      </div>
      <p class="m-0 text-xs text-n-slate-11">
        {{ reference.inbox.name }}
        <template v-if="reference.team">
          {{ t('INTERNAL_COMMUNICATION.REFERENCE.SEPARATOR') }}
          {{ reference.team.name }}
        </template>
        <template v-if="reference.assignee">
          {{ t('INTERNAL_COMMUNICATION.REFERENCE.SEPARATOR') }}
          {{ reference.assignee.name }}
        </template>
      </p>
      <Button
        size="xs"
        variant="link"
        class="justify-self-start"
        @click="openConversation"
      >
        {{ t('INTERNAL_COMMUNICATION.REFERENCE.OPEN') }}
      </Button>
    </div>
    <div v-else class="flex items-center gap-2 text-sm text-n-slate-11">
      <i class="i-lucide-lock size-4" />
      <span>{{ t('INTERNAL_COMMUNICATION.REFERENCE.UNAVAILABLE') }}</span>
    </div>
  </div>
</template>
